/**
 * SourcesList — citation section for the ChatInterface surface (the chat
 * mounted on boards / datasheet / timeseries / interview viewers).
 *
 * Collapsible "Sources · N" disclosure (native <details>/<summary>,
 * collapsed by default) that expands to a numbered, clickable list of
 * the cited sources. Renders the structured `sources` array emitted by
 * BOTH backend paths — kg-agent `KgSourceCollector` and Python
 * `response_kwargs["sources"]` — so document / timeseries / pid_diagram /
 * knowledge_graph all get the identical UI.
 *
 * (Mirrors the chatV2 `SourcesFooter` design; this is the component the
 * legacy ChatInterface actually renders via MessageBubble.)
 */
import { File, FileText, GitFork, Network, TableProperties, ExternalLink, ChevronRight } from 'lucide-react'
import type { MessageSource } from '@/service/api'
import { useTranslation } from '@/i18n'
import type { TFunction } from 'i18next'

const ICON_BY_TYPE = {
  document: File,
  timeseries: TableProperties,
  pid_diagram: GitFork,
  knowledge_graph: Network,
} as const

/** Stable de-dup key so a source cited by multiple tool calls renders once. */
function sourceKey(s: MessageSource): string {
  return (
    s.source_id ??
    `${s.type ?? 'document'}|${s.doc_id ?? s.entity_id ?? s.tag_id ?? s.doc_name ?? s.title ?? ''}`
  )
}

/** Human-readable title. Prefers the backend-supplied `title` (board
 *  document filename / published title), then type-specific fallbacks. */
function sourceTitle(s: MessageSource, t: TFunction): string {
  if (s.title && s.title.trim()) return s.title.trim()
  if (s.doc_name && s.doc_name.trim()) return s.doc_name.trim()
  switch (s.type) {
    case 'document':
      return s.doc_id
        ? t('chat:messages.sources.document_id', { defaultValue: 'Document {{id}}', id: s.doc_id })
        : t('chat:messages.sources.document', { defaultValue: 'Document' })
    case 'timeseries':
      return s.tag_id
        ? t('chat:messages.sources.historian_tag', { defaultValue: 'Historian tag {{id}}', id: s.tag_id })
        : t('chat:messages.sources.historian_data', { defaultValue: 'Historian data' })
    case 'pid_diagram':
      return s.entity_id
        ? t('chat:messages.sources.tag', { defaultValue: 'Tag {{id}}', id: s.entity_id })
        : s.pid_source
          ? t('chat:messages.sources.sheet', { defaultValue: 'Sheet {{id}}', id: s.pid_source })
          : t('chat:messages.sources.pid', { defaultValue: 'P&ID' })
    case 'knowledge_graph':
      return s.entity_id
        ? t('chat:messages.sources.entity', { defaultValue: 'Entity {{id}}', id: s.entity_id })
        : t('chat:messages.sources.knowledge_graph', { defaultValue: 'Knowledge graph' })
    default:
      return t('chat:messages.sources.source', { defaultValue: 'Source' })
  }
}

/** Optional one-line subtitle (page / sheet / range). */
function sourceSubtitle(s: MessageSource, t: TFunction): string | undefined {
  if (s.type === 'document' && s.page_number != null)
    return t('chat:messages.sources.page', { defaultValue: 'Page {{number}}', number: s.page_number })
  if (s.type === 'document' && s.sheet_name) return s.sheet_name
  if (s.type === 'pid_diagram' && s.pid_source && s.entity_id)
    return t('chat:messages.sources.sheet', { defaultValue: 'Sheet {{id}}', id: s.pid_source })
  if (s.content && s.content.trim()) {
    const c = s.content.trim()
    return c.length > 80 ? `${c.slice(0, 80)}…` : c
  }
  return undefined
}

export function SourcesList({ sources }: { sources: MessageSource[] | undefined }) {
  const { t } = useTranslation()
  if (!sources || sources.length === 0) return null

  // De-dupe by stable key, preserve first-seen order.
  const seen = new Set<string>()
  const unique: MessageSource[] = []
  for (const s of sources) {
    if (!s || typeof s !== 'object') continue
    const key = sourceKey(s)
    if (seen.has(key)) continue
    seen.add(key)
    unique.push(s)
  }
  if (unique.length === 0) return null

  const label =
    unique.length === 1
      ? t('chat:messages.sources.label_one', { defaultValue: 'Source · {{count}}', count: unique.length })
      : t('chat:messages.sources.label_other', { defaultValue: 'Sources · {{count}}', count: unique.length })

  return (
    <details
      data-testid="sources-list"
      className="group mt-4 pt-3 border-t border-gray-100 dark:border-gray-800"
    >
      <summary
        data-testid="sources-summary"
        className="flex cursor-pointer list-none items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 [&::-webkit-details-marker]:hidden"
      >
        <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-gray-400 dark:text-gray-500 transition-transform group-open:rotate-90" />
        <span>{label}</span>
      </summary>
      <ol className="mt-2 flex flex-col gap-1.5 list-none m-0 p-0">
        {unique.map((source, index) => {
          const Icon = ICON_BY_TYPE[source.type ?? 'document'] ?? FileText
          const title = sourceTitle(source, t)
          const subtitle = sourceSubtitle(source, t)
          const href = source.url
          const clickable = typeof href === 'string' && href.length > 0

          const inner = (
            <>
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs font-medium flex items-center justify-center">
                {index + 1}
              </span>
              <Icon className="flex-shrink-0 h-4 w-4 text-gray-500 dark:text-dark-text-muted mt-0.5" />
              <span className="flex-1 min-w-0">
                <span className="block text-sm text-gray-700 dark:text-gray-300 truncate">
                  {title}
                </span>
                {subtitle && (
                  <span className="block text-xs text-gray-500 dark:text-dark-text-muted truncate">
                    {subtitle}
                  </span>
                )}
              </span>
              {clickable && (
                <ExternalLink className="flex-shrink-0 h-3 w-3 text-gray-400 dark:text-gray-500 mt-1" />
              )}
            </>
          )

          const rowClass =
            'flex items-start gap-2 p-2 rounded-lg border border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-muted'

          return (
            <li key={`${sourceKey(source)}-${index}`}>
              {clickable ? (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${rowClass} hover:bg-gray-100 dark:hover:bg-dark-accent transition-colors cursor-pointer no-underline`}
                >
                  {inner}
                </a>
              ) : (
                <div className={rowClass}>{inner}</div>
              )}
            </li>
          )
        })}
      </ol>
    </details>
  )
}
